import { create } from "zustand";
import { applyTheme, usePrefs } from "@/lib/prefs";
import {
  DESIGN_KEY,
  DESIGN_THEME_KEY,
  resolveDesign,
  type DesignVersion,
} from "./resolve";

function read(key: string) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string | null) {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* In-memory state still survives client navigation. */
  }
}
export const useDesign = create<{
  version: DesignVersion;
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
}>(() => ({
  version: resolveDesign(window.location.search, read(DESIGN_KEY)),
  theme: read(DESIGN_THEME_KEY) === "dark" ? "dark" : "light",
  setTheme(theme) {
    write(DESIGN_THEME_KEY, theme);
    useDesign.setState({ theme });
    const current = useDesign.getState().version;
    if (current !== "legacy") applyDesign(current);
  },
}));

function applyDesign(version: DesignVersion) {
  const root = document.documentElement;
  if (version !== "legacy") {
    root.dataset.design = version;
    root.style.removeProperty("background-color");
    if (version === "seed") {
      applyTheme(usePrefs.getState().theme);
      return;
    }
    const dark = useDesign.getState().theme === "dark";
    root.classList.toggle("dark", dark);
    root.style.colorScheme = dark ? "dark" : "light";
  } else {
    delete root.dataset.design;
    root.style.removeProperty("background-color");
    applyTheme(usePrefs.getState().theme);
  }
}
let styles: Promise<unknown> | undefined;
let seedStyles: Promise<unknown> | undefined;
export function loadDesignStyles(version: DesignVersion = "revamped") {
  if (version === "seed")
    return (seedStyles ??= import("@/seed/seed.css").catch((error: unknown) => {
      seedStyles = undefined;
      throw error;
    }));
  return (styles ??= import("@/revamped/styles.css").catch((error: unknown) => {
    styles = undefined;
    throw error;
  }));
}
let generation = 0;
let desiredVersion = useDesign.getState().version;
/** Called before routing, then for committed history changes (including POP). */
export async function syncDesign(search: string) {
  const turn = ++generation;
  const version = resolveDesign(search, desiredVersion);
  desiredVersion = version;
  // Save before auth/root redirects can remove the query parameter.
  write(DESIGN_KEY, version !== "legacy" ? version : null);
  if (version !== "legacy") {
    try {
      await loadDesignStyles(version);
    } catch {
      if (turn !== generation) return;
      useDesign.setState({ version: "legacy" });
      applyDesign("legacy");
      return;
    }
  }
  if (turn !== generation) return;
  useDesign.setState({ version });
  applyDesign(version);
}
